const parsedDateCache = new Map();
const weekLabelCache = new Map();
const monthLabelCache = new Map();
const groupedValuesCache = new WeakMap();
const groupedEntryCache = new WeakMap();

/* ================================
   Utils fechas
================================ */

function parseDDMMYYYY(dateStr) {
  if (parsedDateCache.has(dateStr)) {
    return parsedDateCache.get(dateStr);
  }

  const normalizedDate = String(dateStr ?? "").trim();
  const parts = normalizedDate.includes("/")
    ? normalizedDate.split("/")
    : normalizedDate.split("-");

  let dd;
  let mm;
  let yyyy;

  if (parts[0]?.length === 4) {
    [yyyy, mm, dd] = parts.map(Number);
  } else {
    [dd, mm, yyyy] = parts.map(Number);
  }

  const parsedDate = new Date(Date.UTC(yyyy, mm - 1, dd));
  parsedDateCache.set(dateStr, parsedDate);
  return parsedDate;
}

function getISOWeek(date) {
  const temp = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );

  temp.setUTCDate(temp.getUTCDate() + 4 - (temp.getUTCDay() || 7));

  const yearStart = new Date(Date.UTC(temp.getUTCFullYear(), 0, 1));
  return Math.ceil(((temp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function getMonthLabel(date) {
  const meses = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  return `${meses[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

function getWeekLabelFromDay(day) {
  if (weekLabelCache.has(day)) {
    return weekLabelCache.get(day);
  }

  const label = `Semana ${getISOWeek(parseDDMMYYYY(day))}`;
  weekLabelCache.set(day, label);
  return label;
}

function getMonthLabelFromDay(day) {
  if (monthLabelCache.has(day)) {
    return monthLabelCache.get(day);
  }

  const label = getMonthLabel(parseDDMMYYYY(day));
  monthLabelCache.set(day, label);
  return label;
}

/* ================================
   Agregador genérico
================================ */

function aggregateValues(values) {
  const acc = {};
  let sfrSum = 0;
  let sfrCount = 0;
  const tasaUnits = new Set();

  values.forEach((valueObject) => {
    Object.entries(valueObject).forEach(([key, value]) => {
      if (key === "sfr_value") {
        if (typeof value === "number") {
          sfrSum += value;
          sfrCount += 1;
        }
        return;
      }

      if (key === "tasa_units") {
        const rawUnits = Array.isArray(value) ? value : [value];

        rawUnits.forEach((unitValue) => {
          if (typeof unitValue !== "string") return;

          const matches = unitValue.match(/kg\/min|PPM/gi);
          if (matches?.length) {
            matches.forEach((unit) => tasaUnits.add(unit));
            return;
          }

          const normalized = unitValue.trim();
          if (normalized) tasaUnits.add(normalized);
        });
        return;
      }

      if (typeof value === "number") {
        acc[key] = (acc[key] ?? 0) + value;
        return;
      }

      acc[key] = acc[key] ?? value;
    });
  });

  if (sfrCount > 0) {
    acc.sfr_value = Math.round(sfrSum / sfrCount);
  }

  if (tasaUnits.size > 0) {
    const normalizedOrder = ["kg/min", "PPM"].filter((unit) => tasaUnits.has(unit));
    const extraUnits = Array.from(tasaUnits).filter((unit) => !normalizedOrder.includes(unit));
    acc.tasa_units = [...normalizedOrder, ...extraUnits].join(", ");
  }

  return acc;
}

/* ================================
   Groupers
================================ */

function groupEntries(values, resolveLabel) {
  const groupedMap = new Map();

  values.forEach((entry) => {
    if (!entry?.day || !entry?.value) return;

    const label = resolveLabel(entry.day);
    const bucket = groupedMap.get(label);

    if (bucket) {
      bucket.push(entry.value);
      return;
    }

    groupedMap.set(label, [entry.value]);
  });

  return Array.from(groupedMap, ([day, groupedValues]) => ({
    day,
    value: aggregateValues(groupedValues),
  }));
}

function normalizeSummaryObject(values) {
  return Object.entries(values ?? {}).map(([day, value]) => ({ day, value }));
}

function getGroupedValues(values, mode) {
  if (mode === "day") {
    return values ?? [];
  }

  const isArraySummary = Array.isArray(values);
  const isObjectSummary = values && typeof values === "object";

  if (!isArraySummary && !isObjectSummary) {
    return [];
  }

  let modeCache = groupedValuesCache.get(values);
  if (!modeCache) {
    modeCache = new Map();
    groupedValuesCache.set(values, modeCache);
  }

  if (modeCache.has(mode)) {
    return modeCache.get(mode);
  }

  const entries = isArraySummary ? values : normalizeSummaryObject(values);
  const groupedValues =
    mode === "week"
      ? groupEntries(entries, getWeekLabelFromDay)
      : groupEntries(entries, getMonthLabelFromDay);

  const groupedResult = isArraySummary
    ? groupedValues
    : Object.fromEntries(groupedValues.map(({ day, value }) => [day, value]));

  modeCache.set(mode, groupedResult);
  return groupedResult;
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function groupEntry(entry, mode) {
  if (!entry || typeof entry !== "object") {
    return entry;
  }

  let modeCache = groupedEntryCache.get(entry);
  if (!modeCache) {
    modeCache = new Map();
    groupedEntryCache.set(entry, modeCache);
  }

  if (modeCache.has(mode)) {
    return modeCache.get(mode);
  }

  const groupedEntry = { ...entry };

  if (hasOwn(groupedEntry, "values_summary")) {
    // Las curvas y alarmas siguen por día: conservar su preferencia de tasa diaria.
    groupedEntry.daily_values_summary = entry.daily_values_summary || entry.values_summary;
    groupedEntry.values_summary = getGroupedValues(groupedEntry.values_summary, mode);
  } else {
    Object.entries(groupedEntry).forEach(([key, value]) => {
      if (!value || typeof value !== "object" || Array.isArray(value)) return;
      groupedEntry[key] = groupEntry(value, mode);
    });
  }

  modeCache.set(mode, groupedEntry);
  return groupedEntry;
}

/* ================================
   MAIN EXPORT
================================ */

export function groupChartData(data, mode) {
  if (!Array.isArray(data)) return [];
  if (mode === "day") return data;

  return data.map((entry) => groupEntry(entry, mode));
}
